import { createBrowserRouter, redirect } from 'react-router';
import { filter, firstValueFrom } from 'rxjs';
import { isExecutingInDesktop } from '#container/common';
import { WebPlatformShell } from '#features/app/components/web-platform/WebPlatformShell';
import { DesktopRedirecting } from '#features/auth/components/DesktopRedirecting';
import { LogonPage } from '#features/auth/components/LogonPage';
import { FatalRouteErrorBoundary } from '#lib/FatalRouteErrorBoundary';
import { Waiting } from '#lib/Waiting';
import authService from '#services/auth';
import { isLoginInProgress, isUnauthenticated } from '#services/auth/model';
import configService from '#services/configuration';

const waitForResolvedAuthState = () =>
  firstValueFrom(
    authService.authState$.pipe(filter((x) => !isLoginInProgress(x))),
  );

const authLoader = async () => {
  if (isExecutingInDesktop()) {
    return null;
  }

  const authState = await firstValueFrom(authService.authState$);

  if (isLoginInProgress(authState)) {
    return waitForResolvedAuthState();
  }

  if (!isUnauthenticated(authState)) {
    return authState;
  }

  await authService.login();
  return waitForResolvedAuthState();
};

export const routerWeb = createBrowserRouter(
  [
    {
      path: '/',
      loader: authLoader,
      element: isExecutingInDesktop() ? (
        <DesktopRedirecting />
      ) : (
        <WebPlatformShell fallback={<Waiting />} />
      ),
      errorElement: <FatalRouteErrorBoundary />,
    },
    {
      path: '/logon',
      element: <LogonPage />,
      errorElement: <FatalRouteErrorBoundary />,
    },
    // temporary fix to allow existing workspaces to continue loading.
    {
      path: '/web/*',
      loader: ({ request }) => {
        const url = new URL(request.url);

        url.pathname = /^\/web\/?$/.test(url.pathname)
          ? '/web/ccy-matrix/index.html'
          : url.pathname.replace(/\/$/, '') + '/index.html';

        window.location.href = url.href;
        return null;
      },
    },
    {
      // temporary fix to allow existing workspaces to continue loading.
      path: '/desktop/view/*',
      loader: ({ request }) => {
        const url = new URL(request.url);
        url.pathname = url.pathname.replace('/view', '') + 'index.html';
        window.location.href = url.href;
        return null;
      },
    },
    {
      path: '*',
      loader: () => {
        return redirect('/');
      },
    },
  ],
  { basename: configService.env.VITE_BASENAME },
);
